import { __ } from 'i18n/index';
import { MeleeCharacter } from 'components/match/MeleeCharacter';
import { MeleeStage } from 'components/match/MeleeStage';
import { BRAWL_ONLINE_REPLAYS, SLIPPI_REPLAYS } from 'slippi/replayFormats';

// What the friendlies board needs to know about a ladder's game: which
// replays to read, and the name and art for the character and stage ids
// those replays hold. Each catalog has:
//   format                   slippi/replayFormats
//   character(id, color)     {characterId, name, portrait, stock} or null
//   stage(id)                {name, image} or null
//   characterIdForName(name) the replay id of a site character (a main), or null
//   linkTitle(), linkHint()  the "link your folder" card's copy

export function slug(name) {
	return name.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

export function slippiCharacter(characterId, color = 0) {
	const name = MeleeCharacter.characters[characterId];
	if(!name) {
		return null;
	}
	return {
		characterId,
		name,
		portrait: `/images/characters/melee/${slug(name)}.png`,
		stock: `/images/characters/melee/stocks/${slug(name)}/${color || 0}.png`,
	};
}

function meleeStage(stageId) {
	try {
		const stage = MeleeStage.retrieve(stageId);
		return { name: stage.name, image: `/images/stages/melee/${slug(stage.name)}.png` };
	}
	catch(missing) {
		return null;
	}
}

function slippiIdForName(name) {
	const wanted = slug(name || '');
	const found = Object.keys(MeleeCharacter.characters).find(id => slug(MeleeCharacter.characters[id]) === wanted);
	return found === undefined ? null : Number(found);
}

export const meleeCatalog = {
	format: SLIPPI_REPLAYS,
	character: slippiCharacter,
	stage: meleeStage,
	characterIdForName: slippiIdForName,
	linkTitle: () => __('Keep score with your Slippi replays'),
	linkHint: () => __('Link the folder Slippi saves replays to. Only one of you needs to.'),
};

// Brawl Online's ids: P+ v3.2 stage ids and gmCharacterKind character ids,
// from Brawl Online's docs/smashladder/ids.json. Brawl uses the same ids.
const BRAWL_ONLINE_CHARACTERS = {
	0: 'Mario', 1: 'Donkey Kong', 2: 'Link', 3: 'Samus', 4: 'Zero Suit Samus', 5: 'Yoshi', 6: 'Kirby', 7: 'Fox',
	8: 'Pikachu', 9: 'Luigi', 10: 'Captain Falcon', 11: 'Ness', 12: 'Bowser', 13: 'Peach', 14: 'Zelda', 15: 'Sheik',
	16: 'Ice Climbers', 17: 'Ice Climbers', 19: 'Marth', 20: 'Mr. Game & Watch', 21: 'Falco', 22: 'Ganondorf',
	23: 'Wario', 24: 'Meta Knight', 25: 'Pit', 26: 'Olimar', 27: 'Lucas', 28: 'Diddy Kong', 29: 'Pokemon Trainer',
	30: 'Charizard', 31: 'Pokemon Trainer', 32: 'Squirtle', 33: 'Pokemon Trainer', 34: 'Ivysaur', 35: 'King Dedede',
	36: 'Lucario', 37: 'Ike', 38: 'R.O.B.', 39: 'Jigglypuff', 40: 'Toon Link', 41: 'Wolf', 42: 'Snake', 43: 'Sonic',
	50: 'Roy', 51: 'Mewtwo', 53: 'Knuckles',
};

const BRAWL_ONLINE_STAGES = {
	1: 'Battlefield', 2: 'Final Destination', 3: 'Delfino\'s Secret', 4: 'Luigi\'s Mansion', 5: 'Metal Cavern',
	6: 'Bowser\'s Castle', 7: 'Kongo Jungle', 8: 'Rumble Falls', 9: 'Temple of Time', 11: 'Norfair', 12: 'Frigate Husk',
	13: 'Yoshi\'s Island', 14: 'Halberd', 19: 'Lylat Cruise', 20: 'Saffron City', 21: 'Spear Pillar',
	22: 'Port Town Aero Dive', 23: 'Infinite Glacier', 24: 'Flat Zone 2', 25: 'Castle Siege', 28: 'Wario Land',
	29: 'Distant Planet', 30: 'Skyworld', 31: 'Fountain of Dreams', 32: 'Fourside', 33: 'Smashville',
	34: 'Shadow Moses Island', 35: 'Green Hill Zone', 36: 'PictoChat', 37: 'Hanenbow', 41: 'Hyrule Temple',
	42: 'Yoshi\'s Story', 43: 'Jungle Japes', 44: 'Onett', 45: 'Dream Land', 46: 'Pokémon Stadium 2',
	47: 'Peach\'s Castle', 48: 'Corneria', 49: 'Big Blue', 50: 'Planet Zebes', 51: 'Hyrule Castle', 55: 'Training Room',
	64: 'Dracula\'s Castle', 65: 'Mario Circuit', 66: 'Clock Town', 67: 'Sky Sanctuary Zone', 68: 'Dead Line',
	69: 'Dinosaur Land', 70: 'Minecart Madness', 71: 'Golden Temple', 72: 'Bell Tower', 73: 'Ceres Space Colony',
	74: 'Cookie Country', 75: 'Venus Lighthouse', 76: 'Mushroom Kingdom', 77: 'WarioWare, Inc.', 78: 'Subspace',
	79: 'Rainbow Cruise', 80: 'Poké Floats', 81: 'Pirate Ship', 82: 'Venom',
};

// SmashLadder names, normalized, that the game spells differently. Delfino
// Plaza and Pokémon Stadium are Brawl's stages in the slots P+ gave Delfino's
// Secret and Pokémon Stadium 2. The brawl-online branch's
// CreateBrawlOnlineMatches migration has the same list, but for Mr. Game and
// Watch, which it matches by the Brawl ladder's spelling.
const SITE_NAME_ALIASES = {
	ganon: 'ganondorf',
	mrgameandwatch: 'mrgamewatch',
	frigateorpheon: 'frigatehusk',
	porttownaerodrive: 'porttownaerodive',
	temple: 'hyruletemple',
	warioware: 'wariowareinc',
	delfinoplaza: 'delfinossecret',
	pokemonstadium: 'pokemonstadium2',
};

function normalizedName(name) {
	return (name || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

function gameName(siteName) {
	const normalized = normalizedName(siteName);
	return SITE_NAME_ALIASES[normalized] || normalized;
}

function siteThingNamed(things, name) {
	const wanted = normalizedName(name);
	for(const thing of things ? things.values() : []) {
		if(gameName(thing.name) === wanted) {
			return thing;
		}
	}
	return null;
}

// characters and stages are the season's (Maps of site id to Character and
// Stage, or arrays of {name, image_url}; null while they load); their names
// are matched to the game's.
export function brawlOnlineCatalog(characters, stages) {
	return {
		format: BRAWL_ONLINE_REPLAYS,
		character(characterId) {
			const name = BRAWL_ONLINE_CHARACTERS[characterId];
			if(!name) {
				return null;
			}
			const site = siteThingNamed(characters, name);
			const art = site?.image_url || null;
			return { characterId, name: site?.name || name, portrait: art, stock: art };
		},
		stage(stageId) {
			const name = BRAWL_ONLINE_STAGES[stageId];
			if(!name) {
				return null;
			}
			const site = siteThingNamed(stages, name);
			return { name: site?.name || name, image: site?.image_url || null };
		},
		characterIdForName(siteName) {
			const wanted = gameName(siteName);
			const found = Object.keys(BRAWL_ONLINE_CHARACTERS).find(id => normalizedName(BRAWL_ONLINE_CHARACTERS[id]) === wanted);
			return found === undefined ? null : Number(found);
		},
		linkTitle: () => __('Keep score with your Brawl Online replays'),
		linkHint: () => __('Link the folder Brawl Online saves replays to (Documents\\Brawl Online). Only one of you needs to. Games show up when they end.'),
	};
}
